import { useState, useRef, useCallback, useEffect, useMemo, type ReactNode } from "react";
import {
    Upload,
    HardDrive,
    FileText,
    BookOpen,
    Search,
    X,
    Link2,
    File,
    Image,
    Video,
    Music,
    Layers,
    GitBranch,
    Figma,
    ExternalLink,
    Folder,
    FolderOpen,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { GoogleDriveAttachPicker } from "@/components/google-drive/GoogleDriveAttachPicker";
import type { PersistenceContext, PersistedFileResult } from "@/components/GoogleDrivePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMergedKnowledgeDocuments } from "@/hooks/useMergedKnowledgeDocuments";
import { foldersService, type LibraryFolder } from "@/services/foldersService";
import { sourcesService } from "@/services/sourcesService";
import { githubIntegrationService } from "@/services/githubIntegrationService";
import { listRepositories, type GitHubRepo } from "@/services/githubService";
import { startGitHubOAuth, disconnectGitHub } from "@/services/githubOAuthService";
import type { Source } from "@shared/schema";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface AttachedFile {
    type: string;
    data: string;
    filename?: string;
}

interface SelectedRepo {
    owner: string;
    name: string;
    fullName: string;
}

export interface RepoIndexingProgress {
    status: 'idle' | 'indexing' | 'done' | 'error';
    progress: number;
    batchIndex?: number;
    totalBatches?: number;
    chunksIndexed?: number;
    errorMessage?: string;
    phase?: string;
    lastHeartbeat?: string;
    indexStatus?: 'none' | 'pass1Only' | 'partial' | 'complete';
    symbolsStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | null;
}

function formatHeartbeatAge(lastHeartbeat?: string): string | null {
    if (!lastHeartbeat) return null;
    const sec = Math.round((Date.now() - new Date(lastHeartbeat).getTime()) / 1000);
    if (sec < 5) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.round(sec / 60);
    return `${min}m ago`;
}

function formatIndexStatusLabel(indexStatus?: RepoIndexingProgress['indexStatus']): string {
    switch (indexStatus) {
        case 'pass1Only': return 'pass1 only';
        case 'partial': return 'partial';
        case 'complete': return 'complete';
        case 'none': return 'starting';
        default: return '';
    }
}

function renderSymbolsSubLabel(
    prog: RepoIndexingProgress,
    repoName: string
): ReactNode | null {
    if (prog.status !== 'done') return null;

    const sym = prog.symbolsStatus;
    if (sym === 'pending' || sym === 'processing') {
        return (
            <p
                key={`symbols-${repoName}`}
                className="text-[10px] animate-pulse text-amber-600 dark:text-amber-400 px-1"
            >
                <span className="font-medium">{repoName}:</span> Building structural code graph...
            </p>
        );
    }
    if (sym === 'completed') {
        return (
            <p
                key={`symbols-${repoName}`}
                className="text-[10px] text-green-600 dark:text-green-400 px-1"
            >
                <span className="font-medium">{repoName}:</span> ✓ Code index &amp; symbols ready
            </p>
        );
    }
    if (sym === 'skipped') {
        return (
            <p key={`symbols-${repoName}`} className="text-[10px] text-muted-foreground px-1">
                <span className="font-medium">{repoName}:</span> Code index ready
            </p>
        );
    }
    if (sym === 'failed') {
        return (
            <p
                key={`symbols-${repoName}`}
                className="text-[10px] text-amber-700 dark:text-amber-400 px-1"
            >
                <span className="font-medium">{repoName}:</span> Code index ready · symbols failed
            </p>
        );
    }
    return null;
}

function renderRepoIndexingLine(prog: RepoIndexingProgress): string {
    const statusLabel = formatIndexStatusLabel(prog.indexStatus);
    const parts: string[] = [];
    if (prog.status === 'done') {
        parts.push('complete');
    } else {
        if (prog.phase) parts.push(prog.phase);
        if (statusLabel) parts.push(statusLabel);
    }
    if (prog.totalBatches != null && prog.batchIndex != null) {
        parts.push(
            `Batch ${prog.batchIndex}/${prog.totalBatches}${prog.chunksIndexed != null ? `, ${prog.chunksIndexed} chunks` : ''}`
        );
    } else if (parts.length === 0) {
        parts.push('Indexing repo for code search…');
    }
    return parts.join(' · ');
}

interface UnifiedAttachmentPanelProps {
    sources: Source[];
    selectedSourceIds: string[];
    onSourcesChange: (ids: string[]) => void;
    selectedDocumentIds?: string[];
    onDocumentsChange?: (ids: string[]) => void;
    fileAttached: AttachedFile[];
    onFilesChange: (files: AttachedFile[]) => void;
    getContentType: (mimeType: string) => string;
    selectedGitHubRepo?: SelectedRepo | null;
    onGitHubRepoChange?: (repo: SelectedRepo | null) => void;
    selectedGitHubRepos?: SelectedRepo[];
    onToggleGitHubRepo?: (repo: SelectedRepo) => void;
    maxAttachedRepos?: number;
    repoIndexingProgress?: RepoIndexingProgress;
    repoIndexingProgressMap?: Record<string, RepoIndexingProgress>;
    onRefreshRepoIndex?: () => void | Promise<void>;
    persistenceContext?: PersistenceContext;
    onSourcesPersisted?: (sourceIds: string[]) => void;
}

const UnifiedAttachIcon = ({ className }: { className?: string }) => (
    <svg 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className={className}
    >
        <path d="M12 3v12" />
        <path d="M8 11l4 4 4-4" />
        <rect x="3" y="17" width="18" height="4" rx="1" />
        <circle cx="7" cy="19" r="1" fill="currentColor" />
        <circle cx="12" cy="19" r="1" fill="currentColor" />
        <circle cx="17" cy="19" r="1" fill="currentColor" />
    </svg>
);

export function UnifiedAttachmentPanel({
    sources,
    selectedSourceIds,
    onSourcesChange,
    selectedDocumentIds = [],
    onDocumentsChange,
    fileAttached,
    onFilesChange,
    getContentType,
    selectedGitHubRepo,
    onGitHubRepoChange,
    selectedGitHubRepos = selectedGitHubRepo ? [selectedGitHubRepo] : [],
    onToggleGitHubRepo,
    maxAttachedRepos = 5,
    repoIndexingProgress,
    repoIndexingProgressMap,
    onRefreshRepoIndex,
    persistenceContext,
    onSourcesPersisted,
}: UnifiedAttachmentPanelProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [showDrivePicker, setShowDrivePicker] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("upload");
    const [repoInput, setRepoInput] = useState("");
    const [repoSearchQuery, setRepoSearchQuery] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const { toast } = useToast();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const { currentWorkspaceId } = useWorkspace();

    // Check GitHub connection status
    const { data: githubStatus } = useQuery({
        queryKey: ["github-integration-status", user?.id],
        queryFn: async () => {
            if (!user) return { connected: false };
            return await githubIntegrationService.getConnectionStatus();
        },
        enabled: !!user,
        staleTime: 60000,
    });

    // Fetch user's GitHub repos if connected
    const {
        data: userRepos = [],
        isLoading: isLoadingRepos,
        isError: isReposError,
        error: reposError,
        refetch: refetchRepos,
    } = useQuery<GitHubRepo[]>({
        queryKey: ["github-user-repos", user?.id],
        queryFn: () => listRepositories(),
        enabled: !!user && !!githubStatus?.connected,
        staleTime: 300000,
        retry: 1,
    });

    useEffect(() => {
        if (activeTab === "integrations" && githubStatus?.connected && user) {
            void refetchRepos();
        }
    }, [activeTab, githubStatus?.connected, user, refetchRepos]);

    const filteredUserRepos = useMemo(() => {
        const q = repoSearchQuery.trim().toLowerCase();
        if (!q) return userRepos;
        return userRepos.filter(
            (repo) =>
                repo.full_name.toLowerCase().includes(q) ||
                repo.name.toLowerCase().includes(q) ||
                repo.description?.toLowerCase().includes(q)
        );
    }, [userRepos, repoSearchQuery]);

    const attachRepo = (repo: SelectedRepo) => {
        if (onToggleGitHubRepo) {
            const isSelected = selectedGitHubRepos.some((r) => r.fullName === repo.fullName);
            if (!isSelected && selectedGitHubRepos.length >= maxAttachedRepos) {
                toast({
                    title: "Repo limit reached",
                    description: `You can attach up to ${maxAttachedRepos} repositories.`,
                    variant: "destructive",
                });
                return;
            }
            onToggleGitHubRepo(repo);
            toast({
                title: isSelected ? "Repository removed" : "Repository attached",
                description: repo.fullName,
            });
            return;
        }
        onGitHubRepoChange?.(repo);
        toast({ title: "Repository connected", description: repo.fullName });
    };

    const handleConnectGitHub = () => {
        if (!repoInput.trim()) {
            toast({
                title: "Enter repository",
                description: "Please enter a repository in the format owner/repo",
                variant: "destructive",
            });
            return;
        }
        
        const parts = repoInput.trim().split('/');
        if (parts.length !== 2) {
            toast({
                title: "Invalid format",
                description: "Please use the format: owner/repo",
                variant: "destructive",
            });
            return;
        }
        
        const [owner, name] = parts;
        const repo: SelectedRepo = { owner, name, fullName: `${owner}/${name}` };
        attachRepo(repo);
        setRepoInput("");
    };

    const handleClearRepos = () => {
        onGitHubRepoChange?.(null);
        if (onToggleGitHubRepo && selectedGitHubRepos.length) {
            for (const r of [...selectedGitHubRepos]) onToggleGitHubRepo(r);
        }
        toast({ title: "Repositories cleared" });
    };

    const handleConnectGitHubOAuth = async () => {
        try {
            await startGitHubOAuth();
        } catch (error: unknown) {
            toast({
                title: "Connection Failed",
                description: error instanceof Error ? error.message : "Failed to connect GitHub",
                variant: "destructive",
            });
        }
    };

    const handleDisconnectGitHubAccount = async () => {
        try {
            await disconnectGitHub();
            handleClearRepos();
            await queryClient.invalidateQueries({ queryKey: ["github-integration-status"] });
            await queryClient.invalidateQueries({ queryKey: ["github-user-repos"] });
            toast({
                title: "GitHub Disconnected",
                description: "Your GitHub account has been disconnected",
            });
        } catch (error: unknown) {
            toast({
                title: "Disconnect Failed",
                description: error instanceof Error ? error.message : "Failed to disconnect GitHub",
                variant: "destructive",
            });
        }
    };

    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
    const [folderPath, setFolderPath] = useState<LibraryFolder[]>([]);

    const sourceDocsEnabled = isOpen && activeTab === "sourcedocs";

    const {
        ownDocuments: knowledgeItems,
        allDocuments: allKnowledgeItems,
        documentsBySpaceId,
        spaces: librarySpaces,
        isLoading: knowledgeLoading,
        hasNextPage: knowledgeHasNextPage,
        isFetchingNextPage: knowledgeFetchingNextPage,
        fetchNextPage: fetchNextKnowledgePage,
    } = useMergedKnowledgeDocuments({
        enabled: sourceDocsEnabled,
    });

    const { data: folders = [] } = useQuery<LibraryFolder[]>({
        queryKey: ["/api/library-folders", currentWorkspaceId ?? "personal"],
        queryFn: () => foldersService.getFolders(currentWorkspaceId ?? null),
    });

    const { data: folderSources = [], isLoading: folderSourcesLoading } = useQuery<Source[]>({
        queryKey: ["/api/sources-by-folder", currentFolderId],
        queryFn: async () => {
            try {
                return await sourcesService.getSourcesByFolderId(currentFolderId);
            } catch (error) {
                console.error("Error fetching folder sources:", error);
                return [];
            }
        },
        enabled: currentFolderId !== null,
    });

    const childFolders = folders.filter(f => 
        currentFolderId ? f.parentId === currentFolderId : !f.parentId
    ).sort((a, b) => a.sortOrder - b.sortOrder);

    const navigateToFolder = (folder: LibraryFolder) => {
        setFolderPath([...folderPath, folder]);
        setCurrentFolderId(folder.id);
    };

    const navigateUp = () => {
        if (folderPath.length > 0) {
            const newPath = folderPath.slice(0, -1);
            setFolderPath(newPath);
            setCurrentFolderId(newPath.length > 0 ? newPath[newPath.length - 1].id : null);
        }
    };

    const navigateToRoot = () => {
        setFolderPath([]);
        setCurrentFolderId(null);
    };

    const filteredSourcesByFolder = (() => {
        const sourcesToFilter = currentFolderId ? folderSources : sources;
        
        if (!searchQuery) return sourcesToFilter;
        
        return sourcesToFilter.filter(source =>
            source.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            source.content?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    })();

    const totalAttachments = fileAttached.length + selectedSourceIds.length + selectedDocumentIds.length;

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only clear drag state when pointer actually leaves the drop zone (not when moving
        // to the overlay child), otherwise we get rapid flicker between "Attach & Reference" and "Drop files here".
        const related = e.relatedTarget as Node | null;
        if (!dropZoneRef.current?.contains(related)) {
            setIsDragging(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    }, []);

    const processFiles = async (files: File[]) => {
        const validFiles: File[] = [];
        
        for (const file of files) {
            const fileType = file.type;
            const isImage = fileType.startsWith('image/');
            const isAudio = fileType.startsWith('audio/');
            const isVideo = fileType.startsWith('video/');
            const isPDF = fileType === 'application/pdf';
            
            if (!isImage && !isAudio && !isVideo && !isPDF) {
                toast({
                    title: "Unsupported file type",
                    description: `"${file.name}" is not supported. Use images, audio, video, or PDF.`,
                    variant: "destructive",
                });
                continue;
            }

            if (file.size > 35 * 1024 * 1024) {
                toast({
                    title: "File too large",
                    description: `"${file.name}" exceeds 35MB limit.`,
                    variant: "destructive",
                });
                continue;
            }
            
            validFiles.push(file);
        }

        if (validFiles.length === 0) return;

        const readFileAsDataURL = (file: File): Promise<AttachedFile | null> => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64data = reader.result as string;
                    if (base64data) {
                        resolve({
                            type: getContentType(file.type),
                            data: base64data,
                            filename: file.name
                        });
                    } else {
                        resolve(null);
                    }
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(file);
            });
        };

        const newFiles = await Promise.all(validFiles.map(readFileAsDataURL));
        const successfulFiles = newFiles.filter((f): f is AttachedFile => f !== null);
        
        if (successfulFiles.length > 0) {
            onFilesChange([...fileAttached, ...successfulFiles]);
            toast({
                title: "Files attached",
                description: `${successfulFiles.length} file(s) added`,
            });
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        processFiles(files);
        e.target.value = '';
    };

    const handleDriveFilesAttached = async (
        files: Array<{ filename: string; blob: Blob; mimeType: string }>
    ) => {
        const mimeTypeFromName = (filename: string): string => {
            const lower = filename.toLowerCase();
            if (lower.endsWith(".pdf")) return "application/pdf";
            if (lower.endsWith(".png")) return "image/png";
            if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
            if (lower.endsWith(".gif")) return "image/gif";
            if (lower.endsWith(".webp")) return "image/webp";
            if (lower.endsWith(".mp4")) return "video/mp4";
            if (lower.endsWith(".mp3")) return "audio/mpeg";
            return "application/octet-stream";
        };

        const readBlobAsDataURL = (blob: Blob, filename: string): Promise<AttachedFile | null> => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64data = reader.result as string;
                    if (base64data) {
                        resolve({
                            type: getContentType(blob.type || mimeTypeFromName(filename)),
                            data: base64data,
                            filename,
                        });
                    } else {
                        resolve(null);
                    }
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        };

        const newFiles = await Promise.all(
            files.map((file) => readBlobAsDataURL(file.blob, file.filename))
        );
        const successfulFiles = newFiles.filter((f): f is AttachedFile => f !== null);

        if (successfulFiles.length > 0) {
            onFilesChange([...fileAttached, ...successfulFiles]);
        }

        setShowDrivePicker(false);
        setIsOpen(false);
        toast({
            title: "Files attached",
            description: `${successfulFiles.length} file(s) from Google Drive`,
        });
    };

    const toggleSource = (sourceId: string) => {
        if (selectedSourceIds.includes(sourceId)) {
            onSourcesChange(selectedSourceIds.filter(id => id !== sourceId));
        } else {
            onSourcesChange([...selectedSourceIds, sourceId]);
        }
    };

    const toggleDocument = (documentId: string) => {
        if (!onDocumentsChange) return;
        if (selectedDocumentIds.includes(documentId)) {
            onDocumentsChange(selectedDocumentIds.filter(id => id !== documentId));
        } else {
            onDocumentsChange([...selectedDocumentIds, documentId]);
        }
    };

    const removeFile = (index: number) => {
        onFilesChange(fileAttached.filter((_, i) => i !== index));
    };

    const getSourceIcon = (type: string) => {
        switch (type) {
            case 'youtube': return <Video className="h-4 w-4 text-red-500" />;
            case 'article': return <FileText className="h-4 w-4 text-blue-500" />;
            case 'document': return <File className="h-4 w-4 text-orange-500" />;
            case 'link': return <Link2 className="h-4 w-4 text-green-500" />;
            default: return <FileText className="h-4 w-4" />;
        }
    };

    const getFileIcon = (type: string) => {
        if (type.includes('image')) return <Image className="h-4 w-4 text-purple-500" />;
        if (type.includes('video')) return <Video className="h-4 w-4 text-red-500" />;
        if (type.includes('audio')) return <Music className="h-4 w-4 text-green-500" />;
        return <File className="h-4 w-4 text-orange-500" />;
    };

    const filteredKnowledge = knowledgeItems.filter(item =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sharedSpaceSections = useMemo(() => {
        const sections: Array<{ spaceId: string; spaceName: string; docs: typeof knowledgeItems }> = [];
        for (const space of librarySpaces) {
            const docs = documentsBySpaceId.get(space.id) ?? [];
            if (docs.length === 0) continue;
            const filtered = docs.filter(
                (item) =>
                    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.type?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            if (filtered.length > 0) {
                sections.push({
                    spaceId: space.id,
                    spaceName: space.name || "Untitled Space",
                    docs: filtered,
                });
            }
        }
        return sections;
    }, [librarySpaces, documentsBySpaceId, searchQuery]);

    const hasSharedSpaceDocs = sharedSpaceSections.length > 0;

    return (
        <>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 relative rounded-lg"
                                data-testid="unified-attach-button"
                            >
                                <UnifiedAttachIcon className="h-4 w-4" />
                                {totalAttachments > 0 && (
                                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center">
                                        {totalAttachments}
                                    </span>
                                )}
                            </Button>
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="top"><p>Attach files & sources</p></TooltipContent>
                </Tooltip>

                <PopoverContent 
                    className="w-[400px] p-0" 
                    align="start"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div
                        ref={dropZoneRef}
                        className={`relative transition-all ${isDragging ? 'ring-2 ring-primary ring-inset bg-primary/5' : ''}`}
                    >
                        {isDragging && (
                            <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/10 backdrop-blur-sm rounded-lg">
                                <div className="text-center">
                                    <Upload className="h-8 w-8 mx-auto mb-2 text-primary animate-bounce" />
                                    <p className="text-sm font-medium text-primary">Drop files here</p>
                                </div>
                            </div>
                        )}

                        <div className="p-3 border-b">
                            <div className="flex items-center gap-2">
                                <Layers className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium text-sm">Attach & Reference</span>
                            </div>
                        </div>

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <TabsList className="w-full grid grid-cols-4 h-9 p-1 mx-0 rounded-none border-b">
                                <TabsTrigger value="upload" className="text-xs data-[state=active]:bg-background">
                                    <Upload className="h-3 w-3 mr-1" />
                                    Upload
                                </TabsTrigger>
                                <TabsTrigger value="drive" className="text-xs data-[state=active]:bg-background">
                                    <HardDrive className="h-3 w-3 mr-1" />
                                    Drive
                                </TabsTrigger>
                                <TabsTrigger value="sourcedocs" className="text-xs data-[state=active]:bg-background">
                                    <FileText className="h-3 w-3 mr-1" />
                                    Source Docs
                                </TabsTrigger>
                                <TabsTrigger value="integrations" className="text-xs data-[state=active]:bg-background">
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Integrations
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="upload" className="p-3 m-0">
                                <div 
                                    className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                    <p className="text-sm font-medium">Click to upload</p>
                                    <p className="text-xs text-muted-foreground mt-1">or drag & drop files anywhere</p>
                                    <p className="text-xs text-muted-foreground mt-2">Images, PDF, Audio, Video (max 35MB)</p>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,audio/*,video/*,application/pdf"
                                    onChange={handleFileInputChange}
                                    multiple
                                    className="hidden"
                                />
                            </TabsContent>

                            <TabsContent value="drive" className="p-3 m-0">
                                <div 
                                    className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                                    onClick={() => setShowDrivePicker(true)}
                                >
                                    <HardDrive className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                    <p className="text-sm font-medium">Open Google Drive</p>
                                    <p className="text-xs text-muted-foreground mt-1">Select files from your Drive</p>
                                </div>
                            </TabsContent>

                            <TabsContent value="sourcedocs" className="m-0">
                                <div className="p-2 border-b space-y-2">
                                    <div className="relative">
                                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                        <Input
                                            placeholder="Search sources & docs..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="h-8 pl-7 text-sm"
                                            data-testid="input-source-search"
                                        />
                                    </div>
                                    {folderPath.length > 0 && (
                                        <div className="flex items-center gap-1 text-xs">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-1"
                                                onClick={navigateToRoot}
                                                data-testid="button-nav-root"
                                            >
                                                <Folder className="h-3 w-3 mr-1" />
                                                Root
                                            </Button>
                                            {folderPath.map((folder, idx) => (
                                                <span key={folder.id} className="flex items-center">
                                                    <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 px-1"
                                                        onClick={() => {
                                                            setFolderPath(folderPath.slice(0, idx + 1));
                                                            setCurrentFolderId(folder.id);
                                                        }}
                                                    >
                                                        {folder.name}
                                                    </Button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <ScrollArea className="h-[220px]">
                                    <div className="p-2 space-y-1">
                                        {/* Folder Navigation */}
                                        {folderPath.length > 0 && (
                                            <div
                                                onClick={navigateUp}
                                                className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted"
                                                data-testid="button-folder-up"
                                            >
                                                <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">Back</span>
                                            </div>
                                        )}
                                        
                                        {/* Child Folders */}
                                        {childFolders.length > 0 && (
                                            <>
                                                {childFolders.map((folder) => (
                                                    <div
                                                        key={folder.id}
                                                        onClick={() => navigateToFolder(folder)}
                                                        className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted"
                                                        data-testid={`folder-${folder.id}`}
                                                    >
                                                        <FolderOpen className="h-4 w-4 text-amber-500" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{folder.name}</p>
                                                        </div>
                                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                        
                                        {/* Loading indicator for folder sources */}
                                        {currentFolderId && folderSourcesLoading && (
                                            <div className="flex items-center gap-2 p-2 text-muted-foreground">
                                                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                                                <span className="text-xs">Loading sources...</span>
                                            </div>
                                        )}
                                        
                                        {/* Sources Section */}
                                        {/*{!folderSourcesLoading && filteredSourcesByFolder.length > 0 && (
                                            <>
                                                <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1 pt-1 pb-0.5">
                                                    Sources ({filteredSourcesByFolder.length})
                                                </div>
                                                {filteredSourcesByFolder.map((source) => (
                                                    <div
                                                        key={source.id}
                                                        onClick={() => toggleSource(source.id)}
                                                        className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                                                            selectedSourceIds.includes(source.id) 
                                                                ? "bg-primary/10 border border-primary/30" 
                                                                : "hover:bg-muted"
                                                        }`}
                                                        data-testid={`source-${source.id}`}
                                                    >
                                                        <Checkbox
                                                            checked={selectedSourceIds.includes(source.id)}
                                                            className="pointer-events-none"
                                                        />
                                                        {getSourceIcon(source.type)}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{source.title}</p>
                                                            <p className="text-xs text-muted-foreground truncate">{source.type}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}*/}
                                        
                                        {/* Knowledge Base Section */}
                                        {knowledgeLoading && (
                                            <div className="flex items-center gap-2 p-2 text-muted-foreground">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span className="text-xs">Loading documents...</span>
                                            </div>
                                        )}
                                        {filteredKnowledge.length > 0 && !currentFolderId && (
                                            <>
                                                <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1 pt-2 pb-0.5">
                                                    Knowledge Base ({filteredKnowledge.length})
                                                </div>
                                                {filteredKnowledge.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        onClick={() => toggleDocument(item.id)}
                                                        className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                                                            selectedDocumentIds.includes(item.id) 
                                                                ? "bg-primary/10 border border-primary/30" 
                                                                : "hover:bg-muted"
                                                        }`}
                                                        data-testid={`knowledge-${item.id}`}
                                                    >
                                                        <Checkbox
                                                            checked={selectedDocumentIds.includes(item.id)}
                                                            className="pointer-events-none"
                                                        />
                                                        <BookOpen className="h-4 w-4 text-indigo-500" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{item.title}</p>
                                                            <p className="text-xs text-muted-foreground truncate">Knowledge Base</p>
                                                        </div>
                                                    </div>
                                                ))}
                                                {knowledgeHasNextPage && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="w-full mt-1 text-xs"
                                                        onClick={() => fetchNextKnowledgePage()}
                                                        disabled={knowledgeFetchingNextPage}
                                                    >
                                                        {knowledgeFetchingNextPage ? "Loading..." : "Load more"}
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                        
                                        {hasSharedSpaceDocs && !currentFolderId && (
                                            <>
                                                <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1 pt-2 pb-0.5">
                                                    Shared Spaces
                                                </div>
                                                {sharedSpaceSections.map((section) => (
                                                    <div key={section.spaceId} className="space-y-0.5">
                                                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1 pt-1 pb-0.5">
                                                            {section.spaceName} ({section.docs.length})
                                                        </div>
                                                        {section.docs.map((item) => (
                                                            <div
                                                                key={item.id}
                                                                onClick={() => toggleDocument(item.id)}
                                                                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                                                                    selectedDocumentIds.includes(item.id)
                                                                        ? "bg-primary/10 border border-primary/30"
                                                                        : "hover:bg-muted"
                                                                }`}
                                                                data-testid={`knowledge-shared-${item.id}`}
                                                            >
                                                                <Checkbox
                                                                    checked={selectedDocumentIds.includes(item.id)}
                                                                    className="pointer-events-none"
                                                                />
                                                                <BookOpen className="h-4 w-4 text-teal-500" />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium truncate">{item.title}</p>
                                                                    <p className="text-xs text-muted-foreground truncate">{section.spaceName}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </>
                                        )}

                                        {!folderSourcesLoading && filteredSourcesByFolder.length === 0 && filteredKnowledge.length === 0 && !hasSharedSpaceDocs && childFolders.length === 0 && (
                                            <div className="text-center py-6 text-muted-foreground">
                                                <FileText className="h-8 w-8 mx-auto mb-2 opacity-20" />
                                                <p className="text-xs">No sources or docs found</p>
                                            </div>
                                        )}
                                    </div>
                                </ScrollArea>
                            </TabsContent>

                            <TabsContent value="integrations" className="m-0 p-3">
                                <div className="space-y-3">
                                    <p className="text-xs text-muted-foreground">Connect external services to reference in your chat</p>
                                    
                                    {/* GitHub Integration */}
                                    <div className="space-y-2">
                                        <div 
                                            className={`flex items-center gap-3 p-3 rounded-md border transition-colors ${
                                                selectedGitHubRepos.length > 0 ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50'
                                            }`}
                                            data-testid="section-github-integration"
                                        >
                                            <div className="p-2 rounded-md bg-gray-800">
                                                <GitBranch className="h-4 w-4 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium">GitHub</p>
                                                {!githubStatus?.connected ? (
                                                    <p className="text-xs text-muted-foreground">Connect your GitHub account first</p>
                                                ) : selectedGitHubRepos.length > 0 ? (
                                                    <p className="text-xs text-primary font-medium">
                                                        {selectedGitHubRepos.length} repo{selectedGitHubRepos.length > 1 ? 's' : ''} attached
                                                    </p>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground">Select a repository for code analysis</p>
                                                )}
                                            </div>
                                            {selectedGitHubRepos.length > 0 ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 text-xs text-destructive hover:text-destructive"
                                                    onClick={handleClearRepos}
                                                    data-testid="button-clear-github-repos"
                                                >
                                                    <X className="h-3 w-3 mr-1" />
                                                    Clear repos
                                                </Button>
                                            ) : githubStatus?.connected ? (
                                                <Badge variant="secondary" className="text-[9px]">Connected</Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[9px]">Not connected</Badge>
                                            )}
                                        </div>
                                        {selectedGitHubRepos.length > 0 && (
                                            <div className="flex flex-wrap gap-1 px-1">
                                                {selectedGitHubRepos.map((r) => {
                                                    const prog = repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress;
                                                    return (
                                                        <Badge
                                                            key={r.fullName}
                                                            variant="secondary"
                                                            className="text-[10px] cursor-pointer"
                                                            onClick={() => attachRepo(r)}
                                                        >
                                                            {r.fullName}
                                                            {prog?.status === 'indexing' ? ' …' : prog?.status === 'done' ? ' ✓' : ''}
                                                        </Badge>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {selectedGitHubRepos
                                            .filter(r => (repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress)?.status === 'indexing')
                                            .map(r => {
                                                const prog = repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress;
                                                if (!prog || prog.status !== 'indexing') return null;
                                                const isStale =
                                                    prog.status === 'indexing' &&
                                                    prog.lastHeartbeat
                                                    ? Date.now() - new Date(prog.lastHeartbeat).getTime() > 60_000
                                                    : false;
                                                return (
                                                    <div key={r.fullName} className="space-y-1.5 px-1">
                                                        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                                                            <span className="truncate">
                                                                <span className="font-medium">{r.name}:</span>{' '}
                                                                {renderRepoIndexingLine(prog)}
                                                                {prog.lastHeartbeat && (
                                                                    <span className={isStale ? ' text-amber-600 dark:text-amber-400' : ''}>
                                                                        {' · '}
                                                                        {isStale ? 'stale ' : 'heartbeat '}
                                                                        {formatHeartbeatAge(prog.lastHeartbeat)}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="shrink-0 ml-1">{prog.progress}%</span>
                                                        </div>
                                                        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                                            <div
                                                                className="h-full bg-primary transition-all duration-300"
                                                                style={{ width: `${Math.min(100, prog.progress)}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        {selectedGitHubRepos
                                            .map((r) => {
                                                const prog = repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress;
                                                if (!prog) return null;
                                                return renderSymbolsSubLabel(prog, r.name);
                                            })}
                                        {selectedGitHubRepos.every(r => (repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress)?.status === 'done') && selectedGitHubRepos.length > 0 && (
                                            <div className="flex items-center justify-between gap-2 px-1">
                                                <p className="text-[10px] text-muted-foreground">
                                                    {selectedGitHubRepos.some((r) => {
                                                        const sym = (repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress)?.symbolsStatus;
                                                        return sym === 'pending' || sym === 'processing';
                                                    })
                                                        ? 'Code search ready · building code graphs…'
                                                        : 'Code search ready'}
                                                </p>
                                                {onRefreshRepoIndex && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 text-[10px] px-2"
                                                        onClick={() => void onRefreshRepoIndex()}
                                                        data-testid="button-refresh-repo-index"
                                                    >
                                                        Refresh index
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                        {selectedGitHubRepos.some(r => (repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress)?.status === 'error') && (
                                            <div className="space-y-1 px-1">
                                                {selectedGitHubRepos
                                                    .filter(r => (repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress)?.status === 'error')
                                                    .map(r => {
                                                        const prog = repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress;
                                                        return (
                                                            <p key={r.fullName} className="text-[10px] text-destructive/80">
                                                                <span className="font-medium">{r.fullName}:</span>{' '}
                                                                {prog?.errorMessage ?? 'Indexing failed — summary only'}
                                                            </p>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                        
                                        {!githubStatus?.connected ? (
                                            <div className="p-3 rounded-md bg-muted/50 border border-dashed space-y-2">
                                                <p className="text-xs text-muted-foreground">
                                                    Connect your GitHub account to browse and attach repositories.
                                                </p>
                                                <Button
                                                    size="sm"
                                                    className="h-8 w-full"
                                                    onClick={() => void handleConnectGitHubOAuth()}
                                                    data-testid="button-connect-github-oauth"
                                                >
                                                    Connect GitHub
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-[10px] text-muted-foreground px-1">
                                                    Select up to {maxAttachedRepos} repos ({selectedGitHubRepos.length}/{maxAttachedRepos})
                                                </p>

                                                <div className="relative">
                                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                                    <Input
                                                        placeholder="Search repositories..."
                                                        value={repoSearchQuery}
                                                        onChange={(e) => setRepoSearchQuery(e.target.value)}
                                                        className="h-8 text-sm pl-8"
                                                        data-testid="input-github-repo-search"
                                                    />
                                                </div>

                                                {isLoadingRepos ? (
                                                    <div className="flex items-center justify-center py-6">
                                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                                        <span className="text-xs text-muted-foreground ml-2">Loading repositories...</span>
                                                    </div>
                                                ) : isReposError ? (
                                                    <div className="py-4 px-2 text-center space-y-2">
                                                        <p className="text-xs text-destructive">
                                                            {reposError instanceof Error
                                                                ? reposError.message
                                                                : "Failed to load repositories"}
                                                        </p>
                                                        <div className="flex flex-wrap items-center justify-center gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 text-xs"
                                                                onClick={() => void refetchRepos()}
                                                                data-testid="button-retry-github-repos"
                                                            >
                                                                Retry
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                className="h-7 text-xs"
                                                                onClick={() => void handleConnectGitHubOAuth()}
                                                                data-testid="button-reconnect-github-oauth"
                                                            >
                                                                Reconnect GitHub
                                                            </Button>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 text-xs text-muted-foreground"
                                                            onClick={() => void handleDisconnectGitHubAccount()}
                                                            data-testid="button-disconnect-github-account"
                                                        >
                                                            Disconnect account
                                                        </Button>
                                                    </div>
                                                ) : userRepos.length === 0 ? (
                                                    <p className="text-xs text-muted-foreground text-center py-4 px-2">
                                                        No repositories found on your GitHub account.
                                                    </p>
                                                ) : filteredUserRepos.length === 0 ? (
                                                    <p className="text-xs text-muted-foreground text-center py-4 px-2">
                                                        No repositories match your search.
                                                    </p>
                                                ) : (
                                                    <ScrollArea className="h-48 border rounded-md p-2">
                                                        <div className="space-y-1">
                                                            {filteredUserRepos.map((repo) => {
                                                                const isSelected = selectedGitHubRepos.some(
                                                                    (r) => r.fullName === repo.full_name
                                                                );
                                                                return (
                                                                    <div
                                                                        key={repo.id}
                                                                        className={`flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer ${isSelected ? "bg-primary/10" : ""}`}
                                                                        onClick={() => {
                                                                            const [owner, name] = repo.full_name.split("/");
                                                                            attachRepo({ owner, name, fullName: repo.full_name });
                                                                        }}
                                                                        data-testid={`github-repo-row-${repo.full_name}`}
                                                                    >
                                                                        <GitBranch className="h-3 w-3 text-muted-foreground shrink-0" />
                                                                        <span className="text-xs flex-1 truncate">{repo.full_name}</span>
                                                                        {isSelected && <Check className="h-3 w-3 text-primary shrink-0" />}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </ScrollArea>
                                                )}

                                                <div className="text-xs text-muted-foreground text-center pt-1">
                                                    Or enter a repo manually
                                                </div>
                                                <div className="flex gap-2">
                                                    <Input
                                                        placeholder="owner/repo (e.g., facebook/react)"
                                                        value={repoInput}
                                                        onChange={(e) => setRepoInput(e.target.value)}
                                                        className="h-8 text-sm flex-1"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") handleConnectGitHub();
                                                        }}
                                                        data-testid="input-github-repo"
                                                    />
                                                    <Button
                                                        size="sm"
                                                        className="h-8"
                                                        onClick={handleConnectGitHub}
                                                        disabled={!repoInput.trim()}
                                                        data-testid="button-connect-github"
                                                    >
                                                        Connect
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Figma Integration */}
                                    <div 
                                        className="flex items-center gap-3 p-3 rounded-md border hover:bg-muted/50 transition-colors cursor-pointer"
                                        data-testid="section-figma-integration"
                                    >
                                        <div className="p-2 rounded-md bg-purple-600">
                                            <Figma className="h-4 w-4 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">Figma</p>
                                            <p className="text-xs text-muted-foreground">Paste a Figma URL in the chat to analyze</p>
                                        </div>
                                        <Badge variant="secondary" className="text-[9px]">Ready</Badge>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>

                        {(fileAttached.length > 0 || selectedSourceIds.length > 0 || selectedDocumentIds.length > 0) && (
                            <div className="p-2 border-t bg-muted/30">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-muted-foreground">
                                        Attached ({totalAttachments})
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 text-xs text-destructive hover:text-destructive"
                                        onClick={() => {
                                            onFilesChange([]);
                                            onSourcesChange([]);
                                            onDocumentsChange?.([]);
                                        }}
                                    >
                                        Clear all
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {fileAttached.map((file, index) => (
                                        <Badge
                                            key={index}
                                            variant="secondary"
                                            className="gap-1 pr-1 text-xs max-w-[150px]"
                                        >
                                            {getFileIcon(file.type)}
                                            <span className="truncate">{file.filename || 'File'}</span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeFile(index);
                                                }}
                                                className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                    {selectedSourceIds.map((id) => {
                                        const source = sources.find(s => s.id === id);
                                        if (!source) return null;
                                        return (
                                            <Badge
                                                key={id}
                                                variant="secondary"
                                                className="gap-1 pr-1 text-xs max-w-[150px]"
                                            >
                                                {getSourceIcon(source.type)}
                                                <span className="truncate">{source.title}</span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleSource(id);
                                                    }}
                                                    className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </Badge>
                                        );
                                    })}
                                    {selectedDocumentIds.map((id) => {
                                        const kbItem = allKnowledgeItems.find(k => k.id === id);
                                        if (!kbItem) return null;
                                        return (
                                            <Badge
                                                key={id}
                                                variant="secondary"
                                                className="gap-1 pr-1 text-xs max-w-[150px]"
                                            >
                                                <BookOpen className="h-3 w-3 text-indigo-500" />
                                                <span className="truncate">{kbItem.title}</span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleDocument(id);
                                                    }}
                                                    className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </Badge>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            <Dialog open={showDrivePicker} onOpenChange={setShowDrivePicker}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle>Pick from Google Drive</DialogTitle>
                        <DialogDescription>
                            Select files from your Google Drive
                        </DialogDescription>
                    </DialogHeader>
                    <GoogleDriveAttachPicker
                        onFilesAttached={handleDriveFilesAttached}
                        persistToDatabase={!!persistenceContext}
                        persistenceContext={persistenceContext}
                        onFilesPersisted={(files: PersistedFileResult[]) => {
                            const sourceIds = files.map(f => f.sourceId);
                            onSourcesPersisted?.(sourceIds);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}
